import { useEffect, useMemo, useState } from "react";
import { Lock, Unlock, Save, Printer, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/Bits";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/store/AuthStore";
import { RatePrint88 } from "@/components/RatePrint88";
const num = (v) => Number(v) || 0;
const rupee = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const sizeLabel = (it) => `M${it.dia} ${it.thread} ${it.len}`.replace(/\s+/g, " ").trim();

export function Rate88Page() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [basic88, setBasic88] = useState("");
  const [date, setDate] = useState(todayISO());

  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [edits, setEdits] = useState({}); // name -> extra88 string
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rates");
      setItems(data.items || []);
      if (data.basic88 !== undefined && data.basic88 !== null) setBasic88(String(data.basic88));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const b88 = num(basic88);
  const extraStr = (it) => (edits[it.name] !== undefined ? edits[it.name] : (it.extra88 !== undefined ? String(it.extra88) : ""));

  const selected = useMemo(
    () => items.filter((it) => it.cat === "metric" && it.in88 === true),
    [items]
  );

  const printRows = useMemo(
    () => selected.map((it) => ({ ...it, label: sizeLabel(it), rate: b88 + num(extraStr(it)) })),
    [selected, b88, edits]
  );

  const doUnlock = async () => {
    try {
      await api.post("/rates/unlock", { pin });
      setUnlocked(true);
      setPinOpen(false);
      toast.success("Unlocked — you can now edit 8.8 extra costs");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const save = async () => {
    // Re-assert the full current selection (in88 + extra88) so a background
    // data revert cannot silently drop the selected sizes on save.
    const payload = selected.map((it) => ({ name: it.name, in88: true, extra88: num(extraStr(it)) }));
    if (!payload.length) {
      toast.info("No sizes to save");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/rates", { pin, items: payload, basic88: num(basic88) });
      setItems(data.items || []);
      if (data.basic88 !== undefined && data.basic88 !== null) setBasic88(String(data.basic88));
      setEdits({});
      toast.success("8.8 rates saved for all users");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const lock = () => {
    setUnlocked(false);
    setEdits({});
    setPin("");
    toast.info("Locked");
  };

  const doPrint = () => {
    const dd = date ? date.split("-").reverse().join("-") : todayISO().split("-").reverse().join("-");
    const prev = document.title;
    document.title = `TR RATE LIST 8.8 ${dd}`;
    const restore = () => {
      document.title = prev;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
    setTimeout(restore, 1500);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-white">Rate List 8.8</h1>
          <p className="text-sm text-slate-500">High-tensile metric bolts · rates in ₹ per kg</p>
        </div>
        {isAdmin &&
          (unlocked ? (
            <Button data-testid="rate88-lock-btn" onClick={lock} variant="outline" className="rounded-xl border-sky-700 text-sky-300">
              <Unlock className="mr-1.5 h-4 w-4" /> Unlocked
            </Button>
          ) : (
            <Button data-testid="rate88-unlock-btn" onClick={() => setPinOpen(true)} variant="outline" className="rounded-xl border-slate-700 text-slate-200">
              <Lock className="mr-1.5 h-4 w-4" /> Unlock to Edit
            </Button>
          ))}
      </div>

      {/* Basic rate + date */}
      <Card>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-400">8.8 Basic Rate (₹ / kg)</span>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm font-bold text-slate-500">₹</span>
              <Input
                inputMode="decimal"
                data-testid="rate88-basic-input"
                value={basic88}
                onChange={(e) => setBasic88(e.target.value)}
                disabled={!isAdmin}
                placeholder="per kg, e.g. 98"
                className="h-12 rounded-xl border-sky-700/60 bg-slate-900/60 pl-7 text-right font-mono text-lg font-bold text-white focus-visible:ring-sky-500/60 disabled:opacity-70"
              />
            </div>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Date</span>
            <Input
              type="date"
              data-testid="rate88-date-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-12 rounded-xl border-slate-700 bg-slate-900/60 font-mono text-base font-bold text-white focus-visible:ring-sky-500/60"
            />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button data-testid="rate88-print-btn" onClick={doPrint} disabled={!b88 || !selected.length} className="flex-1 rounded-xl bg-sky-500 font-bold text-slate-950 hover:bg-sky-400">
            <Printer className="mr-1.5 h-4 w-4" /> Print
          </Button>
          <Button data-testid="rate88-pdf-btn" onClick={doPrint} disabled={!b88 || !selected.length} variant="outline" className="flex-1 rounded-xl border-sky-700 text-sky-200">
            <FileDown className="mr-1.5 h-4 w-4" /> Save PDF
          </Button>
        </div>
        {unlocked && (
          <div className="mt-3 flex justify-end">
            <Button data-testid="rate88-save-btn" onClick={save} disabled={saving} size="sm" className="rounded-lg bg-emerald-500 font-bold text-slate-950 hover:bg-emerald-400">
              {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />} Save 8.8 Extras
            </Button>
          </div>
        )}
      </Card>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
        </div>
      ) : !selected.length ? (
        <Card className="text-center">
          <p className="py-8 text-sm text-slate-400" data-testid="rate88-empty">
            No sizes selected yet. Go to the <span className="font-bold text-orange-300">Rate</span> tab, unlock, and tick the <span className="font-bold text-sky-300">8.8</span> box on the metric bolt sizes you want to include here.
          </p>
        </Card>
      ) : (
        <Card className="p-0" data-testid="rate88-list">
          <div className="border-b border-slate-800 bg-slate-800/40 px-4 py-2.5 font-display text-sm font-extrabold uppercase tracking-wide text-sky-300">
            Selected Sizes ({selected.length}) — Grade 8.8
          </div>
          <div className="divide-y divide-slate-800/60">
            {selected.map((it) => {
              const extra = extraStr(it);
              const rate = b88 + num(extra);
              return (
                <div key={it.name} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`rate88-row-${it.name}`}>
                  <span className="font-mono text-sm font-semibold text-slate-200">{sizeLabel(it)}</span>
                  <div className="flex items-center gap-2">
                    {unlocked ? (
                      <Input
                        inputMode="decimal"
                        data-testid={`rate88-extra-${it.name}`}
                        value={extra}
                        onChange={(e) => setEdits((p) => ({ ...p, [it.name]: e.target.value }))}
                        placeholder="extra/kg"
                        className="h-8 w-24 rounded-md border-sky-700/60 bg-slate-900/60 text-right font-mono text-xs font-bold text-sky-200"
                      />
                    ) : (
                      <span className="font-mono text-[11px] text-slate-500">+{num(extra)}</span>
                    )}
                    <span data-testid={`rate88-value-${it.name}`} className="w-24 text-right font-mono text-sm font-black text-sky-400">
                      {b88 ? `${rupee(rate)}/kg` : "—"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* PIN unlock dialog */}
      {pinOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPinOpen(false)}>
          <Card className="w-full max-w-xs" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center gap-2 font-display text-lg font-extrabold text-white">
              <Lock className="h-5 w-5 text-sky-400" /> Enter Lock PIN
            </div>
            <Input
              type="password"
              inputMode="numeric"
              data-testid="rate88-pin-input"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doUnlock()}
              placeholder="PIN"
              className="h-12 rounded-xl border-slate-700 bg-slate-900/60 text-center font-mono text-lg tracking-widest text-white"
              autoFocus
            />
            <div className="mt-3 flex gap-2">
              <Button onClick={() => setPinOpen(false)} variant="outline" className="flex-1 rounded-xl border-slate-700 text-slate-300">Cancel</Button>
              <Button data-testid="rate88-pin-submit" onClick={doUnlock} className="flex-1 rounded-xl bg-sky-500 font-bold text-slate-950 hover:bg-sky-400">Unlock</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Print output */}
      <RatePrint88 rows={printRows} basic={b88} date={date} />
    </div>
  );
}
